import { QrCodeScanner, ScaleOutlined } from "@mui/icons-material"
import { Button, CircularProgress, Typography } from "@mui/material"
import React, { useEffect, useState } from "react"
import { CenteredBox, Dialog, Stack } from "src/shared/components"
import OutlinedInput from "src/shared/components/outlined-input"

interface BatchQuantityDialogProps {
  open: boolean
  onClose: () => void
  onConfirm: (openQuantity: number, boxQuantity: number) => void
  lotNumber: string
  loading?: boolean
}

const BatchQuantityDialog: React.FC<BatchQuantityDialogProps> = ({
  open,
  onClose,
  onConfirm,
  lotNumber,
  loading = false
}) => {
  const [openQuantity, setOpenQuantity] = useState<number | null>(null)
  const [boxQuantity, setBoxQuantity] = useState<number | null>(null)
  const [error, setError] = useState<string>("")

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setOpenQuantity(null)
      setBoxQuantity(null)
      setError("")
    }
  }, [open])

  const handleOpenQuantityChange = (value: number) => {
    setOpenQuantity(value)
    if (error) {
      setError("")
    }
  }

  const handleBoxQuantityChange = (value: number) => {
    setBoxQuantity(value)
    if (error) {
      setError("")
    }
  }

  const handleConfirm = () => {
    // Validation
    if (openQuantity === null || boxQuantity === null) {
      setError("Both quantities are required")
      return
    }

    if (isNaN(openQuantity) || openQuantity < 0 || isNaN(boxQuantity) || boxQuantity < 0) {
      setError("Please enter valid quantities (greater than or equal to 0)")
      return
    }

    if (openQuantity === 0 && boxQuantity === 0) {
      setError("At least one quantity must be greater than 0")
      return
    }

    onConfirm(openQuantity, boxQuantity)
  }

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      handleConfirm()
    }
  }

  return (
    <Dialog open={open} toggleDialog={onClose} maxWidth="sm" fullWidth sx={{ "& .MuiPaper-root": { borderRadius: 3 } }}>
      <Stack p={3} spacing={3}>
        <CenteredBox>
          <QrCodeScanner sx={{ fontSize: "3rem", color: "primary.main", mb: 1 }} />
          <Typography variant="h5" gutterBottom>
            Enter Quantities
          </Typography>
          <Typography variant="body2" color="text.secondary" textAlign="center">
            Lot Number: <strong>{lotNumber}</strong>
          </Typography>
        </CenteredBox>

        <OutlinedInput
          label="Open Quantity"
          type="number"
          value={openQuantity}
          onChange={(_, value) => handleOpenQuantityChange(value as number)}
          onKeyDown={handleKeyPress}
          placeholder="Enter open quantity"
          fullWidth
          autoFocus
          error={!!error}
          helperText={error}
          disabled={loading}
          startAdornment={<ScaleOutlined sx={{ mr: 1, color: "text.secondary" }} />}
          inputProps={{
            min: 0,
            step: "any"
          }}
        />

        <OutlinedInput
          label="Box Quantity"
          type="number"
          value={boxQuantity}
          onChange={(_, value) => handleBoxQuantityChange(value as number)}
          onKeyDown={handleKeyPress}
          placeholder="Enter box quantity"
          fullWidth
          error={!!error}
          helperText={error}
          disabled={loading}
          startAdornment={<ScaleOutlined sx={{ mr: 1, color: "text.secondary" }} />}
          inputProps={{
            min: 0,
            step: "any"
          }}
        />

        <Stack direction="row" spacing={2} justifyContent="flex-end">
          <Button variant="outlined" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirm}
            disabled={loading || openQuantity === null || boxQuantity === null}
          >
            {loading ? <CircularProgress size={20} color="inherit" /> : "Confirm"}
          </Button>
        </Stack>
      </Stack>
    </Dialog>
  )
}

export default BatchQuantityDialog
